import { useId, type FC } from "react";
import { getPositiveNumber } from "../deckGeometry";
import type { SupportGraphicProps } from "../finishedDeckSectionGeometry";

export const JFA_SUPPORT_WIDTH_MM = 50;

const OUTLINE_PROPS = {
  stroke: "#111",
  strokeWidth: 0.35,
  strokeLinejoin: "round",
  strokeLinecap: "round",
} as const;

const METAL_COLOR = "#b8b8b8";
const DARK_COLOR = "#545353";
const CENTER_X_MM = JFA_SUPPORT_WIDTH_MM / 2;
const BASE_THICKNESS_MM = 3;
const BELL_THICKNESS_MM = 11;
const FLANGE_WIDTH_MM = 19;
const FLANGE_THICKNESS_MM = 3;
const SCREW_DIAMETER_MM = 8;
const SCREW_THREAD_PITCH_MM = 2.35;
const SCREW_THREAD_RIDGE_HEIGHT_MM = 1.25;
const STEM_SIZE_MM = 8;
const NUT_WIDTH_MM = 10;
const NUT_THICKNESS_MM = 6;
const MIN_THREAD_HEIGHT_MM = 4;
const FIXED_HEIGHT_MM =
  FLANGE_THICKNESS_MM + MIN_THREAD_HEIGHT_MM + STEM_SIZE_MM + NUT_THICKNESS_MM + BELL_THICKNESS_MM + BASE_THICKNESS_MM;

const getSafeHeightMm = (heightMm: number): number =>
  Math.max(getPositiveNumber(heightMm, FIXED_HEIGHT_MM), FIXED_HEIGHT_MM);

const Jfa: FC<SupportGraphicProps> = ({ heightMm, ...props }) => {
  const artworkId = useId().replaceAll(":", "");
  const screwGradientId = `${artworkId}-jfa-screw-gradient`;
  const screwClipId = `${artworkId}-jfa-screw-clip`;
  const drawingHeightMm = getSafeHeightMm(heightMm);
  const flangeX = CENTER_X_MM - FLANGE_WIDTH_MM / 2;
  const screwX = CENTER_X_MM - SCREW_DIAMETER_MM / 2;
  const stemX = CENTER_X_MM - STEM_SIZE_MM / 2;
  const nutX = CENTER_X_MM - NUT_WIDTH_MM / 2;
  const threadHeight = drawingHeightMm - FIXED_HEIGHT_MM + MIN_THREAD_HEIGHT_MM;
  const flangeY = 0;
  const threadY = flangeY + FLANGE_THICKNESS_MM;
  const stemY = threadY + threadHeight;
  const nutY = stemY + STEM_SIZE_MM;
  const bellY = nutY + NUT_THICKNESS_MM;
  const baseY = drawingHeightMm - BASE_THICKNESS_MM;
  const bellTopWidth = 25;
  const bellBottomWidth = 42;
  const bellTopLeftX = CENTER_X_MM - bellTopWidth / 2;
  const bellTopRightX = CENTER_X_MM + bellTopWidth / 2;
  const bellBottomLeftX = CENTER_X_MM - bellBottomWidth / 2;
  const bellBottomRightX = CENTER_X_MM + bellBottomWidth / 2;
  const bellBottomY = bellY + BELL_THICKNESS_MM;
  const screwThreadCount = Math.max(4, Math.ceil((threadHeight + SCREW_THREAD_PITCH_MM * 2) / SCREW_THREAD_PITCH_MM));
  const screwThreadIndexes = Array.from({ length: screwThreadCount }, (_, index) => index);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${JFA_SUPPORT_WIDTH_MM} ${drawingHeightMm}`}
      aria-hidden="true"
      fill="none"
      overflow="visible"
      shapeRendering="geometricPrecision"
      {...props}
    >
      <defs>
        <linearGradient id={screwGradientId} x1={screwX} x2={screwX + SCREW_DIAMETER_MM} y1="0" y2="0">
          <stop offset="0%" stopColor="#777777" />
          <stop offset="18%" stopColor="#c6c6c6" />
          <stop offset="50%" stopColor="#f1f1f1" />
          <stop offset="82%" stopColor="#9b9b9b" />
          <stop offset="100%" stopColor="#595959" />
        </linearGradient>
        <clipPath id={screwClipId}>
          <rect x={screwX} y={threadY} width={SCREW_DIAMETER_MM} height={threadHeight} rx="0.8" />
        </clipPath>
      </defs>

      {/* FLANGIA ORIZZONTALE */}
      <g {...OUTLINE_PROPS} fill={METAL_COLOR}>
        <rect x={flangeX} y={flangeY} width={FLANGE_WIDTH_MM} height={FLANGE_THICKNESS_MM} />
      </g>

      {/* FILETTO */}
      <g>
        <rect
          x={screwX}
          y={threadY}
          width={SCREW_DIAMETER_MM}
          height={threadHeight}
          rx="0.8"
          fill={`url(#${screwGradientId})`}
          {...OUTLINE_PROPS}
        />
        <g clipPath={`url(#${screwClipId})`}>
          {screwThreadIndexes.map((index) => {
            const y = threadY - SCREW_THREAD_PITCH_MM + index * SCREW_THREAD_PITCH_MM;
            const ridgeTopY = y + 0.1;
            const ridgeBottomY = y + SCREW_THREAD_RIDGE_HEIGHT_MM;

            return (
              <g key={index}>
                <path
                  d={`M ${screwX - 0.7} ${ridgeTopY + 0.7}
                    C ${screwX + 1.6} ${ridgeTopY - 0.15}
                      ${screwX + 5.2} ${ridgeTopY + 0.15}
                      ${screwX + SCREW_DIAMETER_MM + 0.7} ${ridgeTopY + 0.95}
                    L ${screwX + SCREW_DIAMETER_MM + 0.7} ${ridgeBottomY + 0.75}
                    C ${screwX + 5.1} ${ridgeBottomY + 0.05}
                      ${screwX + 1.6} ${ridgeBottomY - 0.15}
                      ${screwX - 0.7} ${ridgeBottomY + 0.45}
                    Z`}
                  fill="#8f8f8f"
                  opacity="0.7"
                />
                <path
                  d={`M ${screwX - 0.6} ${ridgeTopY + 0.55}
                    C ${screwX + 1.7} ${ridgeTopY - 0.2}
                      ${screwX + 5.1} ${ridgeTopY}
                      ${screwX + SCREW_DIAMETER_MM + 0.6} ${ridgeTopY + 0.75}`}
                  fill="none"
                  stroke="#2e2e2e"
                  strokeWidth="0.32"
                />
                <path
                  d={`M ${screwX - 0.35} ${ridgeTopY + 1.05}
                    C ${screwX + 1.8} ${ridgeTopY + 0.35}
                      ${screwX + 4.9} ${ridgeTopY + 0.5}
                      ${screwX + SCREW_DIAMETER_MM + 0.35} ${ridgeTopY + 1.18}`}
                  fill="none"
                  stroke="#f7f7f7"
                  strokeOpacity="0.58"
                  strokeWidth="0.2"
                />
              </g>
            );
          })}
          <path
            d={`M ${screwX + 1.05} ${threadY} V ${threadY + threadHeight}`}
            fill="none"
            stroke="#ffffff"
            strokeOpacity="0.32"
            strokeWidth="0.35"
          />
          <path
            d={`M ${screwX + SCREW_DIAMETER_MM - 0.85} ${threadY} V ${threadY + threadHeight}`}
            fill="none"
            stroke="#333333"
            strokeOpacity="0.35"
            strokeWidth="0.32"
          />
        </g>
      </g>

      {/* STELO INFERIORE */}
      <g {...OUTLINE_PROPS} fill={METAL_COLOR}>
        <rect x={stemX} y={stemY} width={STEM_SIZE_MM} height={STEM_SIZE_MM} />
      </g>

      {/* DADO / COLLARE INFERIORE */}
      <g {...OUTLINE_PROPS} fill={METAL_COLOR}>
        <rect x={nutX} y={nutY} width={NUT_WIDTH_MM} height={NUT_THICKNESS_MM} />
        <line x1={CENTER_X_MM} y1={nutY} x2={CENTER_X_MM} y2={nutY + NUT_THICKNESS_MM} />
      </g>

      {/* CAMPANA */}
      <g>
        {/* corpo principale */}
        <path
          d={`M ${bellTopLeftX} ${bellY}
            H ${bellTopRightX}
            Q ${bellTopRightX + 0.2} ${bellY + 5.2} ${bellBottomRightX} ${bellBottomY}
            H ${bellBottomLeftX}
            Q ${bellTopLeftX - 0.2} ${bellY + 5.2} ${bellTopLeftX} ${bellY}
            Z`}
          fill={DARK_COLOR}
          {...OUTLINE_PROPS}
        />

        {/* foro sinistro */}
        <path
          d={`M ${bellBottomLeftX + 1.1} ${bellBottomY - 0.8}
            Q ${bellBottomLeftX + 5.3} ${bellBottomY - 2.1} ${bellBottomLeftX + 8.5} ${bellBottomY - 5.1}
            Q ${bellBottomLeftX + 10} ${bellBottomY - 6.3} ${bellBottomLeftX + 10.4} ${bellBottomY - 5}
            Q ${bellBottomLeftX + 10.9} ${bellBottomY - 3.7} ${bellBottomLeftX + 9.3} ${bellBottomY - 2.5}
            Q ${bellBottomLeftX + 6.2} ${bellBottomY - 0.8} ${bellBottomLeftX + 1.1} ${bellBottomY - 0.8}
            Z`}
          fill={DARK_COLOR}
          {...OUTLINE_PROPS}
        />

        {/* foro centrale */}
        <path
          d={`M ${CENTER_X_MM - 3.6} ${bellBottomY - 2.5}
            Q ${CENTER_X_MM - 2.1} ${bellBottomY - 7.2} ${CENTER_X_MM} ${bellBottomY - 7.5}
            Q ${CENTER_X_MM + 2.1} ${bellBottomY - 7.2} ${CENTER_X_MM + 3.6} ${bellBottomY - 2.5}
            Q ${CENTER_X_MM + 2.2} ${bellBottomY - 1.2} ${CENTER_X_MM} ${bellBottomY - 1.1}
            Q ${CENTER_X_MM - 2.2} ${bellBottomY - 1.2} ${CENTER_X_MM - 3.6} ${bellBottomY - 2.5}
            Z`}
          fill={DARK_COLOR}
          {...OUTLINE_PROPS}
        />

        {/* foro destro */}
        <path
          d={`M ${bellBottomRightX - 10.4} ${bellBottomY - 5}
            Q ${bellBottomRightX - 10} ${bellBottomY - 6.3} ${bellBottomRightX - 8.5} ${bellBottomY - 5.1}
            Q ${bellBottomRightX - 5.3} ${bellBottomY - 2.1} ${bellBottomRightX - 1.1} ${bellBottomY - 0.8}
            Q ${bellBottomRightX - 6.2} ${bellBottomY - 0.8} ${bellBottomRightX - 9.3} ${bellBottomY - 2.5}
            Q ${bellBottomRightX - 10.9} ${bellBottomY - 3.7} ${bellBottomRightX - 10.4} ${bellBottomY - 5}
            Z`}
          fill={DARK_COLOR}
          {...OUTLINE_PROPS}
        />
      </g>

      {/* BASE INFERIORE */}
      <g {...OUTLINE_PROPS} fill={DARK_COLOR}>
        <rect x="0" y={baseY} width={JFA_SUPPORT_WIDTH_MM} height={BASE_THICKNESS_MM} />
      </g>
    </svg>
  );
};

export default Jfa;
